import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { paymentStatusStyles } from '../components/eventDetails/eventDetailsStyles';
import { confirmAttendanceAction, fetchAttendanceEventInfo, sendAttendanceOtp } from '../services/api';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getFirstErrorText = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) {
    for (const item of value) {
      const resolved = getFirstErrorText(item);
      if (resolved) return resolved;
    }
    return '';
  }
  if (typeof value === 'object') {
    for (const nestedValue of Object.values(value)) {
      const resolved = getFirstErrorText(nestedValue);
      if (resolved) return resolved;
    }
  }
  return '';
};

const getApiErrorMessage = (error, fallbackMessage) => {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim()) return data.trim();

  if (data && typeof data === 'object') {
    const priorityKeys = ['otp', 'detail', 'message', 'non_field_errors', 'identifier_value', 'display_pin'];
    for (const key of priorityKeys) {
      const resolved = getFirstErrorText(data[key]);
      if (resolved) return resolved;
    }

    for (const value of Object.values(data)) {
      const resolved = getFirstErrorText(value);
      if (resolved) return resolved;
    }
  }

  return error?.message || fallbackMessage;
};

const getQueryToken = (search) => {
  const params = new URLSearchParams(search || '');
  return String(params.get('token') || '').trim();
};

const normalizeIdentifierType = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === 'phone' || normalized === 'phone_number' ? 'phone' : 'email';
};

const getActionConfig = (mode) => {
  const normalizedMode = String(mode || '').toLowerCase();
  if (normalizedMode === 'checkout') {
    return {
      key: 'checkout',
      title: 'Self Check-out',
      otpButton: 'Complete Check-out',
      sendOtpButton: 'Send Check-out OTP',
      successMessage: 'Check-out completed successfully.',
    };
  }

  return {
    key: 'checkin',
    title: 'Self Check-in',
    otpButton: 'Complete Check-in',
    sendOtpButton: 'Send Check-in OTP',
    successMessage: 'Check-in completed successfully.',
  };
};

const SelfAttendancePage = ({ mode = 'checkin' }) => {
  const location = useLocation();
  const { eventSlug = '' } = useParams();
  const actionConfig = useMemo(() => getActionConfig(mode), [mode]);

  const [loadingInfo, setLoadingInfo] = useState(true);
  const [infoError, setInfoError] = useState('');
  const [eventInfo, setEventInfo] = useState(null);
  const [challengeToken, setChallengeToken] = useState('');
  const [form, setForm] = useState({
    identifierType: 'email',
    identifierValue: '',
    displayPin: '',
    otp: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [completed, setCompleted] = useState(false);
  const [otpMeta, setOtpMeta] = useState({ maskedEmail: '', expiresIn: null });
  const [statusMessage, setStatusMessage] = useState({ severity: '', message: '' });
  const [completedResponse, setCompletedResponse] = useState(null);

  const routeToken = useMemo(() => getQueryToken(location.search), [location.search]);
  const resolvedEventSlug = String(eventSlug || eventInfo?.event_slug || '').trim();

  useEffect(() => {
    if (!routeToken) {
      setLoadingInfo(false);
      setInfoError('Missing token in URL query.');
      return;
    }

    let ignore = false;

    const loadEventInfo = async () => {
      setLoadingInfo(true);
      setInfoError('');

      try {
        const response = await fetchAttendanceEventInfo(routeToken);
        if (ignore) return;

        const info = response.data || {};
        setEventInfo(info);
        setChallengeToken(String(info.challenge_token || routeToken));
        setForm((previousForm) => ({
          ...previousForm,
          identifierType: normalizeIdentifierType(info.identifier_type || previousForm.identifierType),
          identifierValue: String(info.identifier_value || info.email || info.phone || info.phone_number || previousForm.identifierValue || ''),
          displayPin: String(info.display_pin || previousForm.displayPin || ''),
        }));
      } catch (error) {
        if (ignore) return;
        setInfoError(getApiErrorMessage(error, 'Unable to load attendance details.'));
      } finally {
        if (!ignore) setLoadingInfo(false);
      }
    };

    loadEventInfo();

    return () => {
      ignore = true;
    };
  }, [routeToken]);

  const validateBaseFields = () => {
    const nextErrors = {};
    const identifierType = normalizeIdentifierType(form.identifierType);
    const identifierValue = String(form.identifierValue || '').trim();
    const displayPin = String(form.displayPin || '').trim();

    if (!resolvedEventSlug) {
      nextErrors.general = 'Event slug is missing in URL.';
    }
    if (!challengeToken) {
      nextErrors.general = 'Challenge token is missing or expired.';
    }

    if (!identifierValue) {
      nextErrors.identifierValue = identifierType === 'email' ? 'Email is required.' : 'Phone number is required.';
    } else if (identifierType === 'email' && !emailRegex.test(identifierValue)) {
      nextErrors.identifierValue = 'Enter a valid email address.';
    } else if (identifierType === 'phone' && identifierValue.length < 6) {
      nextErrors.identifierValue = 'Enter a valid phone number.';
    }

    if (!displayPin) {
      nextErrors.displayPin = 'PIN is required.';
    }

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildPayload = () => ({
    challenge_token: String(challengeToken || '').trim(),
    identifier_type: normalizeIdentifierType(form.identifierType),
    identifier_value: String(form.identifierValue || '').trim(),
    display_pin: String(form.displayPin || '').trim(),
    mode: actionConfig.key,
  });

  const handleSendOtp = async () => {
    setStatusMessage({ severity: '', message: '' });
    if (!validateBaseFields()) return;

    setOtpSending(true);
    try {
      const response = await sendAttendanceOtp(resolvedEventSlug, buildPayload());
      const responseData = response.data || {};
      const latestChallengeToken = String(responseData.challenge_token || '').trim();
      if (latestChallengeToken) setChallengeToken(latestChallengeToken);

      setOtpMeta({
        maskedEmail: String(responseData.masked_email || ''),
        expiresIn: Number(responseData.expires_in) || null,
      });
      setOtpSent(true);
      setCurrentStep(2);
      setForm((previousForm) => ({ ...previousForm, otp: '' }));
      setStatusMessage({
        severity: 'success',
        message: responseData.message || 'OTP sent successfully.',
      });
    } catch (error) {
      setStatusMessage({
        severity: 'error',
        message: getApiErrorMessage(error, 'Unable to send OTP.'),
      });
    } finally {
      setOtpSending(false);
    }
  };

  const handleCompleteAttendance = async () => {
    setStatusMessage({ severity: '', message: '' });
    if (!validateBaseFields()) return;

    const otp = String(form.otp || '').trim();
    if (!otp) {
      setFormErrors((previousErrors) => ({ ...previousErrors, otp: 'OTP is required.' }));
      return;
    }

    setFormErrors((previousErrors) => ({ ...previousErrors, otp: '' }));

    setOtpVerifying(true);
    try {
      const response = await confirmAttendanceAction(resolvedEventSlug, {
        ...buildPayload(),
        otp,
      });
      const responseData = response.data || {};
      const latestChallengeToken = String(responseData.challenge_token || '').trim();
      if (latestChallengeToken) setChallengeToken(latestChallengeToken);

      setCompletedResponse(responseData);
      setCompleted(true);
      setStatusMessage({
        severity: 'success',
        message: responseData.message || actionConfig.successMessage,
      });
    } catch (error) {
      setStatusMessage({
        severity: 'error',
        message: getApiErrorMessage(error, `Unable to complete ${actionConfig.key}.`),
      });
    } finally {
      setOtpVerifying(false);
    }
  };

  const eventTitle = eventInfo?.event?.title || eventInfo?.event_title || eventInfo?.title || '-';
  const attendeeName = eventInfo?.attendee_name || eventInfo?.attendee?.name || completedResponse?.attendee_name || '-';
  const completedAttendee = completedResponse?.attendee || {};
  const stepHeading = currentStep === 1 ? 'Step 1 of 2' : 'Step 2 of 2';
  const otpExpiresMinutes = Math.round(Number(otpMeta.expiresIn) / 60);

  const inputFieldSx = {
    '& .MuiInputBase-input': { fontSize: '1.1rem', py: 1.5 },
    '& .MuiInputLabel-root': { fontSize: '1rem' },
  };

  return (
    <Box sx={paymentStatusStyles.page}>
      <Container maxWidth="sm" sx={paymentStatusStyles.container}>
        <Card sx={paymentStatusStyles.card}>
          <CardContent sx={paymentStatusStyles.cardContent}>
            <Stack spacing={2.5}>
              <Typography
                variant="h3"
                sx={{ ...paymentStatusStyles.title({ color: '#065f46' }), fontSize: { xs: '2rem', sm: '2.4rem' } }}
              >
                {actionConfig.title}
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: '1.05rem' }}>{eventTitle}</Typography>

              {loadingInfo && (
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <CircularProgress size={22} />
                  <Typography color="text.secondary" sx={{ fontSize: '1.05rem' }}>Loading attendance details...</Typography>
                </Stack>
              )}

              {!loadingInfo && infoError && <Alert severity="error">{infoError}</Alert>}

              {!loadingInfo && !infoError && completed && (
                <Stack spacing={2.5} sx={{ textAlign: 'center', py: 2 }}>
                  <Typography sx={{ fontSize: '4rem', lineHeight: 1 }}>✓</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#065f46' }}>
                    {statusMessage.message || actionConfig.successMessage}
                  </Typography>
                  <Typography sx={{ color: 'text.secondary', fontSize: '1.05rem' }}>
                    {completedAttendee?.name || attendeeName ? `Attendee: ${completedAttendee?.name || attendeeName}` : ''}
                  </Typography>
                  {completedAttendee?.checked_in_at && (
                    <Typography sx={{ color: 'text.secondary', fontSize: '1rem' }}>
                      Checked in at: {completedAttendee.checked_in_at}
                    </Typography>
                  )}
                  {completedAttendee?.checked_out_at && (
                    <Typography sx={{ color: 'text.secondary', fontSize: '1rem' }}>
                      Checked out at: {completedAttendee.checked_out_at}
                    </Typography>
                  )}
                </Stack>
              )}

              {!loadingInfo && !infoError && !completed && (
                <>
                  <Typography sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.95rem' }}>{stepHeading}</Typography>

                  {formErrors.general && <Alert severity="error">{formErrors.general}</Alert>}
                  {statusMessage.message && statusMessage.severity === 'error' && <Alert severity="error">{statusMessage.message}</Alert>}

                  {currentStep === 1 && (
                    <>
                      <ToggleButtonGroup
                        exclusive
                        fullWidth
                        value={form.identifierType}
                        onChange={(_, value) => {
                          if (!value) return;
                          setForm((previousForm) => ({ ...previousForm, identifierType: value }));
                          setFormErrors((previousErrors) => ({ ...previousErrors, identifierValue: '' }));
                        }}
                        sx={{ '& .MuiToggleButton-root': { fontSize: '1rem', py: 1.2, fontWeight: 700 } }}
                      >
                        <ToggleButton value="email">Email</ToggleButton>
                        <ToggleButton disabled value="phone">Phone Number</ToggleButton>
                      </ToggleButtonGroup>

                      <TextField
                        fullWidth
                        label={form.identifierType === 'email' ? 'Email Address' : 'Phone Number'}
                        value={form.identifierValue}
                        onChange={(event) => {
                          setForm((previousForm) => ({ ...previousForm, identifierValue: event.target.value }));
                          setFormErrors((previousErrors) => ({ ...previousErrors, identifierValue: '' }));
                        }}
                        error={Boolean(formErrors.identifierValue)}
                        helperText={formErrors.identifierValue}
                        sx={inputFieldSx}
                      />

                      <TextField
                        fullWidth
                        label="Display PIN"
                        value={form.displayPin}
                        onChange={(event) => {
                          setForm((previousForm) => ({ ...previousForm, displayPin: event.target.value }));
                          setFormErrors((previousErrors) => ({ ...previousErrors, displayPin: '' }));
                        }}
                        error={Boolean(formErrors.displayPin)}
                        helperText={formErrors.displayPin || 'Enter the PIN currently displayed on screen.'}
                        sx={inputFieldSx}
                      />

                      <Button
                        variant="contained"
                        onClick={handleSendOtp}
                        disabled={otpSending || otpVerifying}
                        sx={{ ...paymentStatusStyles.primaryActionButton, py: 1.4, fontSize: '1rem', fontWeight: 700 }}
                      >
                        {otpSending ? 'Sending OTP...' : actionConfig.sendOtpButton}
                      </Button>
                    </>
                  )}

                  {currentStep === 2 && (
                    <>
                      <Typography sx={{ color: 'text.secondary', fontSize: '1rem' }}>
                        Enter the OTP sent to your {form.identifierType === 'email' ? 'email' : 'phone number'}.
                      </Typography>
                      {otpMeta.maskedEmail && (
                        <Typography sx={{ color: 'text.secondary', fontSize: '0.95rem' }}>
                          Sent to: {otpMeta.maskedEmail}
                        </Typography>
                      )}
                      {Number.isFinite(otpExpiresMinutes) && otpExpiresMinutes > 0 && (
                        <Typography sx={{ color: 'text.secondary', fontSize: '0.95rem' }}>
                          OTP expires in {otpExpiresMinutes} minute{otpExpiresMinutes !== 1 ? 's' : ''}.
                        </Typography>
                      )}

                      <TextField
                        fullWidth
                        label="OTP"
                        value={form.otp}
                        onChange={(event) => {
                          setForm((previousForm) => ({ ...previousForm, otp: event.target.value }));
                          setFormErrors((previousErrors) => ({ ...previousErrors, otp: '' }));
                        }}
                        error={Boolean(formErrors.otp)}
                        helperText={formErrors.otp || 'Enter the OTP to continue.'}
                        sx={inputFieldSx}
                      />

                      {statusMessage.message && statusMessage.severity === 'success' && (
                        <Alert severity="success">{statusMessage.message}</Alert>
                      )}

                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                        <Button
                          variant="outlined"
                          onClick={() => setCurrentStep(1)}
                          disabled={otpVerifying}
                          sx={{ ...paymentStatusStyles.secondaryActionButton, py: 1.2, fontSize: '0.95rem', fontWeight: 700 }}
                        >
                          Back
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleCompleteAttendance}
                          disabled={otpVerifying || otpSending || !otpSent}
                          sx={{ ...paymentStatusStyles.primaryActionButton, py: 1.2, fontSize: '1rem', fontWeight: 700 }}
                        >
                          {otpVerifying ? 'Verifying OTP...' : actionConfig.otpButton}
                        </Button>
                      </Stack>
                    </>
                  )}
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default SelfAttendancePage;
